import React, { useState, useEffect, useRef } from "react";
import {
	Card,
	Typography,
	Button,
	Space,
	Spin,
	Alert,
	Row,
	Col,
	Breadcrumb,
	Statistic,
} from "antd";
import {
	PlayCircleOutlined,
	PauseCircleOutlined,
	StepBackwardOutlined,
	StepForwardOutlined,
	BookOutlined,
	ClockCircleOutlined,
	EyeOutlined,
	HomeOutlined,
	ArrowLeftOutlined,
	VideoCameraOutlined,
	LockOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../utilities/useAxios";
import { formatDuration } from "../../utilities/Utils";
import PageTitle from "../PageTitle";
const { Title, Text } = Typography;

const VideoPlayerPage = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	const [progress, setProgress] = useState(0);
	const [watchedRanges, setWatchedRanges] = useState([]);
	const [video, setVideo] = useState(null);
	const [course, setCourse] = useState(null);
	const [lastWatchedPosition, setLastWatchedPosition] = useState(0);

	// Simplified progress tracking state
	const [segmentStartTime, setSegmentStartTime] = useState(null);

	const videoRef = useRef(null);
	const progressIntervalRef = useRef(null);
	const hasInitialized = useRef(false);
	const segmentStartTimeRef = useRef(null);
	const isSeekingRef = useRef(false);
	const lastStablePlaybackTimeRef = useRef(0);
	const navigate = useNavigate();
	const { courseId, videoId } = useParams();

	const updateSegmentStartTime = (value) => {
		segmentStartTimeRef.current = value;
		setSegmentStartTime(value);
	};

	useEffect(() => {
		if (hasInitialized.current) return;

		if (courseId && videoId) {
			fetchVideoData();
			hasInitialized.current = true;
		}
		return () => {
			cleanupVideoTracking();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- cleanupVideoTracking/fetchVideoData intentionally run only on courseId/videoId change
	}, [courseId, videoId]);

	// Handle page visibility changes
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden && segmentStartTime !== null) {
				// User switched away from the page, save current progress
				saveCurrentProgress();
			}
		};

		const handleBeforeUnload = () => {
			// User is leaving the page, save current progress
			if (segmentStartTime !== null) {
				saveCurrentProgress();
			}
		};

		document.addEventListener(
			"visibilitychange",
			handleVisibilityChange
		);
		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange
			);
			window.removeEventListener(
				"beforeunload",
				handleBeforeUnload
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- saveCurrentProgress is stable, only segmentStartTime matters
	}, [segmentStartTime]);

	// Cleanup function for video tracking
	const cleanupVideoTracking = async () => {
		clearProgressInterval();
		if (segmentStartTimeRef.current !== null) {
			await saveCurrentProgress();
		}
		updateSegmentStartTime(null);
	};

	const fetchVideoData = async () => {
		try {
			setLoading(true);

			// Save current progress before switching videos
			if (segmentStartTimeRef.current !== null) {
				await saveCurrentProgress();
			}

			// Reset tracking state for new video
			updateSegmentStartTime(null);

			// Get course videos to find the specific video
			const response = await useAxios.get(
				`/cma/courses/${courseId}/videos/`
			);
			const videoData = response.data.find(
				(v) => v.id.toString() === videoId
			);

			if (videoData) {
				// Check if video is locked
				if (videoData.is_locked) {
					setError(
						`이 영상은 ${videoData.unlock_week}주차에 공개됩니다.`
					);
					setVideo(videoData);
					setCourse({
						id: courseId,
						name:
							videoData.lecture_name?.split(
								" - "
							)[0] || "강의",
						book: { title: "교재" },
					});
					return;
				}

				setVideo(videoData);
				setCourse({
					id: courseId,
					name:
						videoData.lecture_name?.split(
							" - "
						)[0] || "강의",
					book: { title: "교재" },
				});
				// Load existing progress
				await loadVideoProgress();
			} else {
				setError("영상을 찾을 수 없습니다.");
			}
		} catch (error) {
			console.error("Error fetching video data:", error);
			if (error.response?.status === 403) {
				setError(
					error.response.data?.error ||
						"이 영상은 아직 잠겨있습니다."
				);
			} else {
				setError(
					"영상 정보를 불러오는데 실패했습니다."
				);
			}
		} finally {
			setLoading(false);
		}
	};

	const loadVideoProgress = async () => {
		try {
			const response = await useAxios.get(
				`/cma/courses/${courseId}/videos/${videoId}/progress/`
			);
			if (response.data.watched_ranges) {
				setWatchedRanges(response.data.watched_ranges);
			}
			if (response.data.last_watched_position) {
				setLastWatchedPosition(
					response.data.last_watched_position
				);
			}
		} catch (error) {
			console.error("Error loading video progress:", error);
		}
	};

	const startProgressTracking = (forceStart = false) => {
		clearProgressInterval();

		// Don't start tracking if user is seeking (unless forced)
		if (isSeekingRef.current && !forceStart) return;

		// Set the start time for the current segment
		if (segmentStartTimeRef.current === null && videoRef.current) {
			const startTime = videoRef.current.currentTime;
			lastStablePlaybackTimeRef.current = startTime;
			updateSegmentStartTime(startTime);
		}

		// Start the 10-second interval
		progressIntervalRef.current = setInterval(() => {
			if (
				videoRef.current &&
				!videoRef.current.paused &&
				!isSeekingRef.current
			) {
				const currentTime =
					videoRef.current.currentTime;
				lastStablePlaybackTimeRef.current = currentTime;
				setCurrentTime(currentTime);
				setDuration(videoRef.current.duration);

				// Every 10 seconds, save progress and reset start time
				if (segmentStartTimeRef.current !== null) {
					saveCurrentProgress();
				}
			}
		}, 10000); // 10 seconds
	};

	const clearProgressInterval = () => {
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
			progressIntervalRef.current = null;
		}
	};

	const saveCurrentProgress = async ({
		force = false,
		endTimeOverride = null,
	} = {}) => {
		if (!videoRef.current || segmentStartTimeRef.current === null)
			return;
		if (isSeekingRef.current && !force)
			return;

		try {
			const currentTime =
				typeof endTimeOverride === "number"
					? endTimeOverride
					: videoRef.current.currentTime;
			const duration = videoRef.current.duration;
			const segmentStart = segmentStartTimeRef.current;

			// Create a range from segment start to current time
			const watchedRange = {
				start: Math.floor(segmentStart),
				end: Math.ceil(currentTime),
				timestamp: new Date().toISOString(),
			};

			// Only send if the range has meaningful duration (more than 1 second)
			if (watchedRange.end - watchedRange.start > 1) {
				// Validate the range
				if (
					watchedRange.start >= 0 &&
					watchedRange.end <= duration &&
					watchedRange.start < watchedRange.end
				) {
					await useAxios.post(
						`/cma/courses/${courseId}/videos/${videoId}/progress/update/`,
						{
							watched_ranges: [
								watchedRange,
							],
							last_watched_position:
								Math.floor(
									currentTime
								),
						}
					);

					// Reload progress to get updated ranges
					await loadVideoProgress();

					// Calculate overall progress
					const totalWatchedTime =
						calculateTotalWatchedTime(
							watchedRanges,
							duration
						);
					const progressPercentage = Math.round(
						(totalWatchedTime / duration) *
							100
					);
					setProgress(progressPercentage);
				}
			}

			// Reset segment start time to current time for next segment
			updateSegmentStartTime(currentTime);
		} catch (error) {
			console.error("Error saving progress:", error);
		}
	};

	const calculateTotalWatchedTime = (ranges, totalDuration) => {
		let totalWatched = 0;
		ranges.forEach((range) => {
			totalWatched += range.end - range.start;
		});
		return Math.min(totalWatched, totalDuration);
	};

	const handlePlayPause = async () => {
		if (videoRef.current) {
			if (isPlaying) {
				// Pause: stop interval and save current progress
				videoRef.current.pause();
				clearProgressInterval();
				await saveCurrentProgress();
				setIsPlaying(false);
			} else {
				// Play: start tracking from current position
				videoRef.current.play();
				setIsPlaying(true);
				startProgressTracking();
			}
		}
	};

	const handleTimeNavigation = async (direction) => {
		if (videoRef.current) {
			// Stop current interval and save progress
			clearProgressInterval();
			await saveCurrentProgress();

			// Navigate to new position
			const newTime =
				currentTime +
				(direction === "forward" ? 5 : -5);
			const clampedTime = Math.max(
				0,
				Math.min(newTime, duration)
			);

			videoRef.current.currentTime = clampedTime;
			setCurrentTime(clampedTime);

			// Start new segment from the new position
			updateSegmentStartTime(clampedTime);

			// Restart interval if video is playing
			if (isPlaying) {
				startProgressTracking();
			}
		}
	};

	const handleVideoEnded = async () => {
		setIsPlaying(false);
		clearProgressInterval();

		// Save any remaining progress
		if (segmentStartTimeRef.current !== null) {
			await saveCurrentProgress();
		}

		// Mark video as completed
		const completionRange = {
			start: 0,
			end: Math.floor(duration),
			timestamp: new Date().toISOString(),
		};

		try {
			await useAxios.post(
				`/cma/courses/${courseId}/videos/${videoId}/progress/update/`,
				{
					watched_ranges: [completionRange],
					last_watched_position:
						Math.floor(duration),
				}
			);

			// Reload progress and set to 100%
			await loadVideoProgress();
			setProgress(100);
		} catch (error) {
			console.error(
				"Error updating completion progress:",
				error
			);
		}
	};

	const handleVideoLoadedMetadata = () => {
		if (videoRef.current) {
			const duration = videoRef.current.duration;
			setDuration(duration);

			// Set video to start from last watched position if available
			if (
				lastWatchedPosition > 0 &&
				lastWatchedPosition < duration
			) {
				videoRef.current.currentTime =
					lastWatchedPosition;
				lastStablePlaybackTimeRef.current =
					lastWatchedPosition;
				setCurrentTime(lastWatchedPosition);
			} else {
				lastStablePlaybackTimeRef.current = 0;
			}
		}
	};

	const handleVideoTimeUpdate = () => {
		if (videoRef.current && !isSeekingRef.current) {
			const time = videoRef.current.currentTime;
			lastStablePlaybackTimeRef.current = time;
			setCurrentTime(time);
		}
	};

	const handleVideoSeeking = async () => {
		if (!videoRef.current || isSeekingRef.current) return;

		// 사용자 시크 시작: 현재 세그먼트를 현재 위치까지만 저장하고 추적 중단
		clearProgressInterval();
		isSeekingRef.current = true;
		await saveCurrentProgress({
			force: true,
			// 시크 중 currentTime은 이미 점프한 위치일 수 있어
			// 마지막 안정 재생 위치를 구간 종료점으로 사용한다.
			endTimeOverride: lastStablePlaybackTimeRef.current,
		});
		updateSegmentStartTime(null);
	};

	const handleVideoSeeked = () => {
		// 시크 완료: 새 위치를 기준으로 새 세그먼트 시작
		if (videoRef.current) {
			const currentTime = videoRef.current.currentTime;
			lastStablePlaybackTimeRef.current = currentTime;
			setCurrentTime(currentTime);
			updateSegmentStartTime(currentTime);

			// 시크 상태 해제 후 재생 중이면 추적 재시작
			isSeekingRef.current = false;
			if (isPlaying) {
				startProgressTracking(true);
			}
		}
	};

	const handleBackToVideoList = () => {
		navigate(`/cma/courses/${courseId}/videos`);
	};

	const handleBackToEnrollments = () => {
		navigate("/cma");
	};

	// Breadcrumb items configuration
	const breadcrumbItems = [
		{
			title: (
				<Button
					type="link"
					icon={<HomeOutlined />}
					onClick={handleBackToEnrollments}
					style={{ padding: 0 }}>
					나의 강의실
				</Button>
			),
		},
		{
			title: (
				<Button
					type="link"
					icon={<VideoCameraOutlined />}
					onClick={handleBackToVideoList}
					style={{ padding: 0 }}>
					강의 영상 목록
				</Button>
			),
		},
		{
			title: video?.name || "영상",
		},
	];

	if (loading) {
		return (
			<div style={{ textAlign: "center", padding: "50px" }}>
				<Spin size="large" />
			</div>
		);
	}

	if (error || !video) {
		return (
			<div style={{ padding: "24px" }}>
				<Alert
					message={
						video?.is_locked
							? "영상 잠금"
							: "오류"
					}
					description={
						error ||
						"영상을 찾을 수 없습니다."
					}
					type={
						video?.is_locked
							? "warning"
							: "error"
					}
					icon={
						video?.is_locked ? (
							<LockOutlined />
						) : null
					}
					showIcon
					action={
						<Button
							size="small"
							onClick={
								handleBackToVideoList
							}>
							영상 목록으로 돌아가기
						</Button>
					}
				/>
			</div>
		);
	}

	return (
		<div style={{ padding: "24px" }}>
			<PageTitle title="기독사관학교 - 온라인 강의 시청" />
			{/* Breadcrumb Navigation */}
			<Breadcrumb
				items={breadcrumbItems}
				style={{ marginBottom: "16px" }}
			/>

			{/* Page Header */}
			<Card style={{ marginBottom: "24px" }}>
				<Row gutter={[16, 16]} align="middle">
					<Col flex="auto">
						<Title
							level={2}
							style={{ margin: 0 }}>
							<PlayCircleOutlined
								style={{
									marginRight:
										"8px",
								}}
							/>
							{video.name}
						</Title>
						<Text type="secondary">
							{course?.name}
						</Text>
					</Col>
					<Col>
						<Space>
							<Button
								icon={
									<ArrowLeftOutlined />
								}
								onClick={
									handleBackToVideoList
								}>
								영상 목록으로
							</Button>
							<Button
								type="primary"
								icon={
									<ArrowLeftOutlined />
								}
								onClick={
									handleBackToEnrollments
								}>
								수강 신청
								내역으로
							</Button>
						</Space>
					</Col>
				</Row>
			</Card>

			{/* Error Display */}
			{error && (
				<Alert
					message="오류"
					description={error}
					type="error"
					showIcon
					className="mb-3"
				/>
			)}

			{/* Video Information Cards */}
			<Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
				<Col span={12}>
					<Card>
						<Statistic
							title="재생 시간"
							value={formatDuration(
								video.duration
							)}
							prefix={
								<ClockCircleOutlined />
							}
						/>
					</Card>
				</Col>
				<Col span={12}>
					<Card>
						<Statistic
							title="진도율"
							value={progress}
							suffix="%"
							prefix={<EyeOutlined />}
							valueStyle={{
								color:
									progress >=
									80
										? "#52c41a"
										: progress >=
										  50
										? "#faad14"
										: "#2457e6",
							}}
						/>
					</Card>
				</Col>
			</Row>
			<Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
				{/* Textbook Link */}
				{video.textbook_url && (
					<Card style={{ width: "100%" }}>
						<Title
							level={4}
							style={{
								marginBottom:
									"16px",
							}}>
							교재
						</Title>
						<Button
							type="primary"
							size="large"
							icon={<BookOutlined />}
							href={
								video.textbook_url
							}
							target="_blank">
							교재 보기
						</Button>
					</Card>
				)}
			</Row>
			{/* Video Player */}
			<Card style={{ marginBottom: "24px" }}>
				<Title
					level={4}
					style={{ marginBottom: "16px" }}>
					영상 재생
				</Title>
				{video.is_locked ? (
					<div
						style={{
							textAlign: "center",
							padding: "60px 20px",
							background: "#f5f5f5",
							borderRadius: "8px",
						}}>
						<LockOutlined
							style={{
								fontSize: "64px",
								color: "#ff4d4f",
								marginBottom:
									"16px",
							}}
						/>
						<Title level={4}>
							이 영상은{" "}
							{video.unlock_week}
							주차에 공개됩니다
						</Title>
						<Text type="secondary">
							영상은 주차별로
							순차적으로 공개됩니다.
						</Text>
					</div>
				) : (
					<div style={{ textAlign: "center" }}>
						<video
							ref={videoRef}
							controls
							style={{
								width: "100%",
								maxHeight: "600px",
							}}
							onLoadedMetadata={
								handleVideoLoadedMetadata
							}
							onTimeUpdate={
								handleVideoTimeUpdate
							}
							onEnded={
								handleVideoEnded
							}
							onSeeking={
								handleVideoSeeking
							}
							onSeeked={
								handleVideoSeeked
							}
							onPlay={() => {
								setIsPlaying(
									true
								);
								startProgressTracking();
							}}
							onPause={() => {
								setIsPlaying(
									false
								);
								clearProgressInterval();
								saveCurrentProgress();
							}}>
							<source
								src={
									video.video_url
								}
								type="video/mp4"
							/>
							Your browser does not
							support the video tag.
						</video>
					</div>
				)}

				{/* Video Controls */}
				{!video.is_locked && (
					<Row
						gutter={[16, 16]}
						style={{ marginTop: "16px" }}
						justify="center">
						<Col>
							<Button
								type="primary"
								size="large"
								icon={
									isPlaying ? (
										<PauseCircleOutlined />
									) : (
										<PlayCircleOutlined />
									)
								}
								onClick={
									handlePlayPause
								}>
								{isPlaying
									? "일시정지"
									: "재생"}
							</Button>
						</Col>
						<Col>
							<Button
								size="large"
								icon={
									<StepBackwardOutlined />
								}
								onClick={() =>
									handleTimeNavigation(
										"backward"
									)
								}>
								5초 뒤로
							</Button>
						</Col>
						<Col>
							<Button
								size="large"
								icon={
									<StepForwardOutlined />
								}
								onClick={() =>
									handleTimeNavigation(
										"forward"
									)
								}>
								5초 앞으로
							</Button>
						</Col>
					</Row>
				)}
			</Card>

			{/* Debug: Watched Ranges Display
			<Card style={{ marginBottom: "24px" }}>
				<Title
					level={4}
					style={{ marginBottom: "16px" }}>
					디버그: 시청 범위 (Watched Ranges)
				</Title>
				<div style={{ marginBottom: "16px" }}>
					<Text strong>현재 재생 위치: </Text>
					<Tag color="blue">
						{formatTime(currentTime)}
					</Tag>
					<Text
						strong
						style={{ marginLeft: "16px" }}>
						세그먼트 시작:{" "}
					</Text>
					<Tag color="green">
						{segmentStartTime
							? formatTime(
									segmentStartTime
							  )
							: "없음"}
					</Tag>
					<Text
						strong
						style={{ marginLeft: "16px" }}>
						마지막 시청 위치:{" "}
					</Text>
					<Tag color="orange">
						{formatTime(
							lastWatchedPosition
						)}
					</Tag>
				</div>

				<div style={{ marginBottom: "16px" }}>
					<Text strong>
						저장된 시청 범위 (
						{watchedRanges.length}개):
					</Text>
					{watchedRanges.length > 0 ? (
						<div
							style={{
								marginTop: "8px",
							}}>
							{watchedRanges.map(
								(
									range,
									index
								) => (
									<Tag
										key={
											index
										}
										color="purple"
										style={{
											margin: "2px",
										}}>
										[
										{formatTime(
											range.start
										)}{" "}
										-{" "}
										{formatTime(
											range.end
										)}

										]
										(
										{Math.round(
											range.end -
												range.start
										)}
										초)
									</Tag>
								)
							)}
						</div>
					) : (
						<Text type="secondary">
							아직 시청 범위가
							없습니다.
						</Text>
					)}
				</div>

				<div>
					<Text strong>총 시청 시간: </Text>
					<Tag color="red">
						{formatTime(
							calculateTotalWatchedTime(
								watchedRanges,
								duration
							)
						)}{" "}
						/ {formatTime(duration)}
					</Tag>
					<Text
						strong
						style={{ marginLeft: "16px" }}>
						진도율:{" "}
					</Text>
					<Tag color="red">{progress}%</Tag>
				</div>
			</Card> */}
		</div>
	);
};

export default VideoPlayerPage;
